import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toggleMenu } from '../redux/slices/uiSlice';
import Footer from '../components/Footer';

const MainLayout = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const isMenuOpen = useSelector((state) => state.ui.isMenuOpen);
  const [isServicesDropdownOpen, setIsServicesDropdownOpen] = useState(false);
  const [isMedicalServicesOpen, setIsMedicalServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileMedicalServicesOpen, setMobileMedicalServicesOpen] = useState(false);
  const servicesDropdownRef = useRef(null);
  const medicalServicesDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setIsServicesDropdownOpen(false);
        setIsMedicalServicesOpen(false);
      }
      if (medicalServicesDropdownRef.current && !medicalServicesDropdownRef.current.contains(event.target)) {
        setIsMedicalServicesOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const servicesData = [
    {
      title: 'Modular Operation Theatre (MOT)',
      path: '/services/modular-ot',
      subItems: [
        { title: 'Minor OT with SS Panel', path: '/services/modular-ot/minor-ot-ss-panel' },
        { title: 'Full OT integration', path: '/services/modular-ot/full-ot-integration' },
      ],
    },
    {
      title: 'Central Sterile Supply Dept (CSSD)',
      path: '/services/cssd',
      subItems: [
        { title: 'Theatre Sterile Supply Unit (TSSU)', path: '/services/cssd/tssu' },
      ],
    },
    {
      title: 'Medical Gas pipeline system (MGPS) with LMO Tank and Vaporizer',
      path: '/services/mgps',
      subItems: [
        { title: 'Oxygen Generation Plant System', path: '/services/mgps/oxygen-generation-plant' },
      ],
    },
    { title: 'Nursing call system (NCS)', path: '/services/ncs' },
    { title: 'Pneumatic tube System (PTS)', path: '/services/pts' },
    { title: 'Advanced Inventory Management System (AIMS)', path: '/services/aims' },
    { title: 'Pneumatic Waste & Laundry Collection System (PWLCS)', path: '/services/pwlcs' },
    { title: 'Laundry Equipments', path: '/services/laundry-equipments' },
    { title: 'Kitchen Equipments', path: '/services/kitchen-equipments' },
    { title: 'Mortuary Equipment', path: '/services/mortuary-equipment' },
    { title: 'Curtain Track System and Guard System', path: '/services/curtain-track-guard' },
    { title: 'Queue management System (QMS)', path: '/services/qms' },
    {
      title: 'Modular ICU',
      path: '/services/modular-icu',
      subItems: [
        { title: 'Modular Emergency', path: '/services/modular-icu/emergency' },
      ],
    },
    { title: 'Medical & Non Medical Furniture', path: '/services/furniture' },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Top Banner */}
      <div className="bg-[#2C2C90] text-white py-2 text-center">
        <p>Hospital Build Pvt Ltd</p>
      </div>

      {/* Header */}
      <header className="bg-white shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4">
          {/* Mobile: Logo, Search, Hamburger in one row */}
          <div className="flex items-center justify-between md:hidden w-full">
            <Link to="/" className="flex-shrink-0">
              <img 
                src="/images/logo.png" 
                alt="MR Medical Gas Solutions" 
                className="h-12 w-auto"
              />
            </Link>
            <button
              className="ml-2 text-gray-700 hover:text-[#2C2C90] focus:outline-none"
              onClick={() => dispatch(toggleMenu())}
              aria-label="Toggle menu"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Desktop: Logo + Nav + Search + Hamburger */}
          <div className="hidden md:flex flex-row items-center w-full relative">
            {/* Logo */}
            <Link to="/" className="flex-shrink-0 mr-8">
              <img 
                src="/images/logo.png" 
                alt="MR Medical Gas Solutions" 
                className="h-20 w-auto"
              />
            </Link>

            {/* Desktop Navigation - centered */}
            <nav className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center space-x-8 text-xl">
              <Link 
                to="/" 
                className={`text-gray-700 hover:text-[#2C2C90] ${location.pathname === '/' ? 'text-[#2C2C90]' : ''}`}
              >
                Home
              </Link>
              <div className="relative group">
                <Link 
                  to="/products" 
                  className={`text-gray-700 hover:text-[#2C2C90] ${location.pathname === '/products' ? 'text-[#2C2C90]' : ''}`}
                >
                  Products
                </Link>
              </div>
              
              {/* Services Dropdown */}
              <div 
                className="relative group"
                ref={servicesDropdownRef}
              >
                <button 
                  onClick={() => setIsServicesDropdownOpen(!isServicesDropdownOpen)}
                  className={`text-gray-700 hover:text-[#2C2C90] inline-flex items-center ${location.pathname.startsWith('/services') ? 'text-[#2C2C90]' : ''}`}
                >
                  Services
                  <svg className="ml-2 h-4 w-4 transform group-hover:rotate-180 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isServicesDropdownOpen && (
                  <div className="absolute z-50 left-0 mt-2 w-72 bg-white rounded-md shadow-lg py-1 transition-all duration-200 origin-top-left">
                    {/* Medical Services Option */}
                    <div 
                      className="relative"
                      ref={medicalServicesDropdownRef}
                    >
                      <button
                        onClick={() => setIsMedicalServicesOpen(!isMedicalServicesOpen)}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-700 flex items-center justify-between"
                      >
                        Medical Services
                        <svg className={`ml-2 h-4 w-4 transform ${isMedicalServicesOpen ? 'rotate-180' : 'rotate-0'} transition-transform duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {isMedicalServicesOpen && (
                        <div className="absolute left-full top-0 ml-1 w-80 bg-white rounded-md shadow-lg py-1 border border-gray-200 max-h-96 overflow-y-auto custom-scrollbar">
                          {servicesData.map((service, index) => (
                            <div key={index}>
                              <Link 
                                to={service.path}
                                className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-700"
                                onClick={() => {
                                  setIsServicesDropdownOpen(false);
                                  setIsMedicalServicesOpen(false);
                                }}
                              >
                                {service.title}
                              </Link>
                              {service.subItems && service.subItems.length > 0 && (
                                <div className="ml-4">
                                  {service.subItems.map((subItem, subIndex) => (
                                    <Link
                                      key={subIndex}
                                      to={subItem.path}
                                      className="block px-4 py-1 text-xs text-gray-600 hover:bg-gray-100 hover:text-blue-700"
                                      onClick={() => {
                                        setIsServicesDropdownOpen(false);
                                        setIsMedicalServicesOpen(false);
                                      }}
                                    >
                                      • {subItem.title}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Hospital Planning & Designing Option */}
                    <Link 
                      to="/services/hospital-planning-designing"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-700"
                      onClick={() => setIsServicesDropdownOpen(false)}
                    >
                      Hospital Planning & Designing
                    </Link>
                    
                    {/* Fire & Safety Option */}
                    <Link 
                      to="/services/fire-safety"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-700"
                      onClick={() => setIsServicesDropdownOpen(false)}
                    >
                      Fire & Safety
                    </Link>
                  </div>
                )}
              </div>
              <Link 
                to="/about" 
                className={`text-gray-700 hover:text-[#2C2C90] ${location.pathname === '/about' ? 'text-[#2C2C90]' : ''}`}
              >
                About us
              </Link>
              <Link 
                to="/blog" 
                className={`text-gray-700 hover:text-[#2C2C90] ${location.pathname === '/blog' ? 'text-[#2C2C90]' : ''}`}
              >
                Blog
              </Link>
              <Link 
                to="/contact" 
                className={`text-gray-700 hover:text-[#2C2C90] ${location.pathname === '/contact' ? 'text-[#2C2C90]' : ''}`}
              >
                Contact Us
              </Link>
              <Link 
                to="/career" 
                className={`text-gray-700 hover:text-[#2C2C90] ${location.pathname === '/career' ? 'text-[#2C2C90]' : ''}`}
              >
                Career
              </Link>
            </nav>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="fixed inset-0 bg-white z-40 flex flex-col items-center justify-start pt-8 space-y-6 md:hidden transition-all duration-300">
              {/* Logo, Search box, and Close (cross) icon in one row */}
              <div className="w-full flex items-center justify-between px-4 mb-6">
                <Link to="/" onClick={() => dispatch(toggleMenu())}>
                  <img src="/images/logo.png" alt="MR Medical Gas Solutions" className="h-12 w-auto" />
                </Link>
                <button
                  className="ml-2 text-gray-700 hover:text-[#2C2C90] focus:outline-none"
                  onClick={() => dispatch(toggleMenu())}
                  aria-label="Close menu"
                >
                  <svg
                    className="w-8 h-8"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {/* Navigation links */}
              <Link to="/" className="text-2xl font-bold text-[#2C2C90]" onClick={() => dispatch(toggleMenu())}>Home</Link>
              <Link to="/products" className="text-2xl font-bold text-[#2C2C90]" onClick={() => dispatch(toggleMenu())}>Products</Link>
              <div className="w-full flex flex-col items-center">
                <button
                  onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                  className="text-2xl font-bold text-[#2C2C90] flex items-center justify-center w-full"
                >
                  Services
                  <svg className={`h-5 w-5 ml-2 transform ${mobileServicesOpen ? 'rotate-180' : 'rotate-0'} transition-transform duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {mobileServicesOpen && (
                  <div className="w-full flex flex-col items-center space-y-2 mt-2">
                    {/* Medical Services Option */}
                    <div className="w-full flex flex-col items-center">
                      <button
                        onClick={() => setMobileMedicalServicesOpen(!mobileMedicalServicesOpen)}
                        className="text-lg text-gray-700 hover:text-[#00B300] flex items-center"
                      >
                        Medical Services
                        <svg className={`h-4 w-4 ml-2 transform ${mobileMedicalServicesOpen ? 'rotate-180' : 'rotate-0'} transition-transform duration-200`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {mobileMedicalServicesOpen && (
                        <div className="w-full flex flex-col items-center space-y-1 mt-2 ml-4 custom-scrollbar max-h-96 overflow-y-auto">
                          {servicesData.map((service, index) => (
                            <div key={index} className="w-full flex flex-col items-center">
                              <Link
                                to={service.path}
                                className="text-base text-gray-600 hover:text-[#00B300]"
                                onClick={() => dispatch(toggleMenu())}
                              >
                                {service.title}
                              </Link>
                              {service.subItems && service.subItems.length > 0 && (
                                <div className="ml-4 space-y-1">
                                  {service.subItems.map((subItem, subIndex) => (
                                    <Link
                                      key={subIndex}
                                      to={subItem.path}
                                      className="text-sm text-gray-500 hover:text-[#00B300] block"
                                      onClick={() => dispatch(toggleMenu())}
                                    >
                                      • {subItem.title}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Hospital Planning & Designing Option */}
                    <Link
                      to="/services/hospital-planning-designing"
                      className="text-lg text-gray-700 hover:text-[#00B300]"
                      onClick={() => dispatch(toggleMenu())}
                    >
                      Hospital Planning & Designing
                    </Link>
                    
                    {/* Fire & Safety Option */}
                    <Link
                      to="/services/fire-safety"
                      className="text-lg text-gray-700 hover:text-[#00B300]"
                      onClick={() => dispatch(toggleMenu())}
                    >
                      Fire & Safety
                    </Link>
                  </div>
                )}
              </div>
              <Link to="/about" className="text-2xl font-bold text-[#2C2C90]" onClick={() => dispatch(toggleMenu())}>About us</Link>
              <Link to="/blog" className="text-2xl font-bold text-[#2C2C90]" onClick={() => dispatch(toggleMenu())}>Blog</Link>
              <Link to="/contact" className="text-2xl font-bold text-[#2C2C90]" onClick={() => dispatch(toggleMenu())}>Contact Us</Link>
              <Link to="/career" className="text-2xl font-bold text-[#2C2C90]" onClick={() => dispatch(toggleMenu())}>Career</Link>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24">
        {children}
      </main>

      <Footer />
    </div>
  );
};

export default MainLayout; 